import { ChangeEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import useAuth from "@/hooks/useAuth";
import propertiesApi from "@/api/propertiesApi";
import PropertyResponse from "@/model/PropertyResponse";
import propertyForm from "@/utils/propertyForm";
import "@/components/PropertyImagesEditor/PropertyImagesEditor.scss";

interface PropertyImagesEditorProps {
  property: PropertyResponse;
  onChange: (property: PropertyResponse) => void;
}

function PropertyImagesEditor({ property, onChange }: PropertyImagesEditorProps) {
  const { t } = useTranslation();
  const { session } = useAuth();
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!session || !property.id) {
    return null;
  }

  const propertyId = property.id;
  const imageUrls = property.imageUrls ?? [];

  const handleFiles = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = "";
    if (files.length === 0) {
      return;
    }

    setIsUploading(true);
    setError(null);
    try {
      for (const file of files) {
        const slot = await propertiesApi.createImageUpload(
          session.accessToken,
          propertyId
        );
        if (slot.uploadUrl) {
          await propertiesApi.uploadImage(slot.uploadUrl, file);
        }
      }
    } catch {
      setError(t("properties.images.uploadError"));
    } finally {
      // The image slots are already saved on the property, even if an upload failed.
      try {
        onChange(
          await propertiesApi.getPropertyById(session.accessToken, propertyId)
        );
      } catch {
        setError(t("properties.images.uploadError"));
      }
      setIsUploading(false);
    }
  };

  const saveImageUrls = async (nextImageUrls: string[]) => {
    setIsSaving(true);
    setError(null);
    try {
      onChange(
        await propertiesApi.updateProperty(
          session.accessToken,
          propertyId,
          propertyForm.requestFromProperty(property, nextImageUrls)
        )
      );
    } catch {
      setError(t("properties.images.saveError"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemove = (url: string) => {
    if (window.confirm(t("properties.images.removeConfirm"))) {
      saveImageUrls(imageUrls.filter((imageUrl) => imageUrl !== url));
    }
  };

  const handleMakeCover = (url: string) => {
    saveImageUrls([url, ...imageUrls.filter((imageUrl) => imageUrl !== url)]);
  };

  const isBusy = isUploading || isSaving;

  return (
    <section className="property-images-editor">
      <div className="property-images-editor__header">
        <h2>{t("properties.images.title")}</h2>
        <label className="property-images-editor__upload">
          {isUploading
            ? t("properties.images.uploading")
            : t("properties.images.addButton")}
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handleFiles}
            disabled={isBusy}
            aria-label={t("properties.images.addButton")}
          />
        </label>
      </div>

      {error && <p className="property-images-editor__error">{error}</p>}

      {imageUrls.length === 0 ? (
        <p className="property-images-editor__empty">
          {t("properties.images.empty")}
        </p>
      ) : (
        <ul className="property-images-editor__grid">
          {imageUrls.map((url, index) => (
            <li key={url} className="property-images-editor__item">
              <img src={url} alt={t("properties.images.alt", { index: index + 1 })} />
              {index === 0 && (
                <span className="property-images-editor__cover">
                  {t("properties.images.cover")}
                </span>
              )}
              <div className="property-images-editor__actions">
                {index > 0 && (
                  <button
                    type="button"
                    onClick={() => handleMakeCover(url)}
                    disabled={isBusy}
                  >
                    {t("properties.images.makeCover")}
                  </button>
                )}
                <button
                  type="button"
                  className="property-images-editor__remove"
                  onClick={() => handleRemove(url)}
                  disabled={isBusy}
                >
                  {t("properties.images.remove")}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export default PropertyImagesEditor;
export type { PropertyImagesEditorProps };
